// src/pages/tienda/MarcasTiendasPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSpinner, FaPlus, FaStore, FaTags, FaUsers, FaTrash, FaEdit, FaQrcode, FaUserPlus, FaGift, FaPalette, FaImage, FaUpload, FaCreditCard, FaCog, FaCode, FaCopy, FaGlobe, FaEnvelope, FaFacebook, FaInstagram, FaTwitter, FaLinkedin, FaTiktok, FaYoutube, FaWhatsapp, FaChartLine, FaStar, FaBuilding, FaSearch, FaInfoCircle, FaSave, FaMobileAlt } from 'react-icons/fa';
import useAuthStore from '../../store/authStore';
import { get, post, patch, del } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import ManagePremiosModal from '../../components/modals/ManagePremiosModal';
import TrabajadorModal from '../../components/modals/TrabajadorModal';
import FranquiciadoModal from '../../components/modals/FranquiciadoModal';
import WorkerFranchiseSelectorModal from '../../components/modals/WorkerFranchiseSelectorModal';
import CreateStoreModal from '../../components/modals/CreateStoreModal';
import QrPdfModal from '../../components/modals/QrPdfModal';
import CreateMarcaModal from '../../components/modals/CreateMarcaModal';
import EditMarcaModal from '../../components/modals/EditMarcaModal';
import LogotipoModal from '../../components/modals/LogotipoModal';
import PointsRulesConfigModal from '../../components/modals/PointsRulesConfigModal';
import PointsStampsConfigModal from '../../components/modals/PointsStampsConfigModal';
import EmbedFormOptionsModal from '../../components/modals/EmbedFormOptionsModal';
import MobileAppAccessModal from '../../components/modals/MobileAppAccessModal';

// Función helper para obtener el símbolo de moneda
const getCurrencySymbol = (currency) => {
    const symbols = {
        'EUR': '€',
        'USD': '$',
        'PEN': 'S/',
        'CLP': '$'
    };
    return symbols[currency] || '$';
};

const MarcasTiendasPage = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [loading, setLoading] = useState(true);

    // Estados para marcas
    const [marcas, setMarcas] = useState([]);
    const [showMarcaModal, setShowMarcaModal] = useState(false);
    const [showEditMarcaModal, setShowEditMarcaModal] = useState(false);
    const [showModalPremio, setShowModalPremio] = useState(false);
    const [selectedMarca, setSelectedMarca] = useState(null);
    const [showLogotipoModal, setShowLogotipoModal] = useState(false);
    const [marcaForLogotipo, setMarcaForLogotipo] = useState(null);
    const [showNewMarcaLogotipoModal, setShowNewMarcaLogotipoModal] = useState(false);
    const [newMarcaCreated, setNewMarcaCreated] = useState(null);

    // Estados para tiendas
    const [tiendas, setTiendas] = useState([]);
    const [showStoreModal, setShowStoreModal] = useState(false);
    const [showTrabajadorModal, setShowTrabajadorModal] = useState(false);
    const [showFranquiciadoModal, setShowFranquiciadoModal] = useState(false);
    const [showWorkerFranchiseSelector, setShowWorkerFranchiseSelector] = useState(false);
    const [showQrModal, setShowQrModal] = useState(false);
    const [selectedTiendaId, setSelectedTiendaId] = useState('');

    // Estados para TPV
    const [showTpvModal, setShowTpvModal] = useState(false);
    const [showTpvConfigModal, setShowTpvConfigModal] = useState(false);
    const [tpvConfigurations, setTpvConfigurations] = useState({});
    const [tpvStats, setTpvStats] = useState({});

    // Estados para Embed
    const [showEmbedModal, setShowEmbedModal] = useState(false);
    const [showEmbedFormOptionsModal, setShowEmbedFormOptionsModal] = useState(false);
    const [embedConfig, setEmbedConfig] = useState(null);

    // Estados para configuración de puntos
    const [showPointsConfigModal, setShowPointsConfigModal] = useState(false);
    const [marcaForPointsConfig, setMarcaForPointsConfig] = useState(null);

    // Estados para marketing
    const [showMarketingModal, setShowMarketingModal] = useState(false);
    const [marcaForMarketing, setMarcaForMarketing] = useState(null);
    const [showGoogleMyBusinessModal, setShowGoogleMyBusinessModal] = useState(false);
    const [tiendaForGMB, setTiendaForGMB] = useState(null);

    // Estados para accesos móviles
    const [showMobileAppAccessModal, setShowMobileAppAccessModal] = useState(false);
    const [tiendaForMobileAccess, setTiendaForMobileAccess] = useState(null);



    useEffect(() => {
        if (user) {
            fetchData();
        }
    }, [user]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [marcasData, tiendasData] = await Promise.all([
                get('marcas'),
                get('tiendas')
            ]);
            setMarcas(marcasData);
            setTiendas(tiendasData);

            // Cargar configuraciones TPV para cada tienda
            await fetchTpvData(tiendasData);

            // Cargar configuración de embed global
            await fetchEmbedData();
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTpvData = async (tiendasData) => {
        try {
            const tpvPromises = tiendasData.map(async (tienda) => {
                try {
                    const [configurations, stats] = await Promise.all([
                        get(`tpv/tiendas/${tienda._id}/configurations`).catch(() => []),
                        get(`tpv/tiendas/${tienda._id}/stats`).catch(() => null)
                    ]);
                    return {
                        tiendaId: tienda._id,
                        configurations,
                        stats
                    };
                } catch (error) {
                    console.warn(`Error loading TPV data for tienda ${tienda._id}:`, error);
                    return {
                        tiendaId: tienda._id,
                        configurations: [],
                        stats: null
                    };
                }
            });

            const tpvData = await Promise.all(tpvPromises);
            const configurationsMap = {};
            const statsMap = {};

            tpvData.forEach(({ tiendaId, configurations, stats }) => {
                configurationsMap[tiendaId] = configurations;
                if (stats) {
                    statsMap[tiendaId] = stats;
                }
            });

            setTpvConfigurations(configurationsMap);
            setTpvStats(statsMap);
        } catch (error) {
            console.error('Error fetching TPV data:', error);
        }
    };

    const fetchEmbedData = async () => {
        try {
            // Obtener configuración de embed global para el usuario
            const embedConfig = await get('embed/global').catch(() => null);
            setEmbedConfig(embedConfig);
        } catch (error) {
            console.error('Error fetching embed data:', error);
        }
    };

    // Handlers para marcas
    const handleCreateMarca = async (marcaData) => {
        try {
            const marcaCreada = await post('marcas', marcaData);
            fetchData();

            // Después de crear la marca, mostrar modal de logotipo
            setNewMarcaCreated(marcaCreada);
            setShowNewMarcaLogotipoModal(true);

            return marcaCreada;
        } catch (error) {
            throw error;
        }
    };

    const handleUploadLogotipo = async (marcaId, logotipoFile) => {
        try {
            const formData = new FormData();
            formData.append('file', logotipoFile);

            const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:3000/api';
            const url = `${baseUrl}/marcas/${marcaId}/logotipo`;

            let token = null;
            try {
                const storedData = localStorage.getItem('auth-storage-petitprime');
                if (storedData) {
                    const parsedData = JSON.parse(storedData);
                    token = parsedData?.state?.token;
                }
            } catch (error) {
                console.warn('Error accessing localStorage:', error);
            }

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                },
                body: formData
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error del servidor: ${response.status} - ${errorText}`);
            }

            await response.json();
            fetchData();
            return true;
        } catch (error) {
            // Fallback: usar XMLHttpRequest
            try {
                const baseUrl = process.env.REACT_APP_BASE_API_URL || 'http://localhost:3000/api';
                const fallbackUrl = `${baseUrl}/marcas/${marcaId}/logotipo`;
                const fallbackFormData = new FormData();
                fallbackFormData.append('file', logotipoFile);

                let fallbackToken = null;
                try {
                    const storedData = localStorage.getItem('auth-storage-petitprime');
                    if (storedData) {
                        const parsedData = JSON.parse(storedData);
                        fallbackToken = parsedData?.state?.token;
                    }
                } catch (tokenError) {
                    console.warn('Error accessing localStorage:', tokenError);
                }

                await uploadWithXHR(fallbackUrl, fallbackFormData, fallbackToken);
                fetchData();
                return true;
            } catch (xhrError) {
                throw error;
            }
        }
    };

    const handleEditMarca = async (marcaData, marcaId) => {
        try {
            await patch(`marcas/${marcaId}`, marcaData);
            fetchData();
            Swal.fire({
                title: 'Éxito',
                text: 'Marca actualizada correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });
        } catch (error) {
            throw error;
        }
    };



    const handleDeleteMarca = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        });
        if (result.isConfirmed) {
            try {
                await del(`marcas/${id}`);
                fetchData();
                Swal.fire({
                    title: 'Eliminado',
                    text: 'La marca ha sido eliminada.',
                    icon: 'success',
                    confirmButtonText: 'Aceptar',
                });
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: error.message,
                    icon: 'error',
                    confirmButtonText: 'Aceptar',
                });
            }
        }
    };

    const handleManagePremios = (marca) => {
        setSelectedMarca(marca);
        setShowModalPremio(true);
    };

    const handleOpenEditMarca = (marca) => {
        setSelectedMarca(marca);
        setShowEditMarcaModal(true);
    };

    const handleChangeLogotipo = (marca) => {
        setMarcaForLogotipo(marca);
        setShowLogotipoModal(true);
    };

    const handleConfigurePoints = (marca) => {
        setMarcaForPointsConfig(marca);
        setShowPointsConfigModal(true);
    };

    const handleSavePointsConfig = async (configData) => {
        try {
            await patch(`marcas/${marcaForPointsConfig._id}`, configData);
            fetchData();
            return true;
        } catch (error) {
            throw error;
        }
    };

    // Handlers para marketing
    const handleManageMarketing = (marca) => {
        setMarcaForMarketing(marca);
        setShowMarketingModal(true);
    };

    const handleSaveMarketingInfo = async (marketingData, marcaId) => {
        try {
            await patch(`marcas/${marcaId}`, marketingData);
            fetchData();
            Swal.fire({
                title: 'Éxito',
                text: 'Información de marketing actualizada correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });
            return true;
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'Error al actualizar la información',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
            throw error;
        }
    };

    const handleManageGoogleMyBusiness = (tiendaId) => {
        const tienda = tiendas.find(t => t._id === tiendaId);
        setTiendaForGMB(tienda);
        setShowGoogleMyBusinessModal(true);
    };

    const handleSaveGoogleMyBusiness = async (gmbData, tiendaId) => {
        try {
            await patch(`tiendas/${tiendaId}`, gmbData);
            fetchData();
            return true;
        } catch (error) {
            throw error;
        }
    };

    // Handlers para accesos móviles
    const handleManageMobileAppAccess = (tiendaId) => {
        const tienda = tiendas.find(t => t._id === tiendaId);
        setTiendaForMobileAccess(tienda);
        setShowMobileAppAccessModal(true);
    };

    // Handlers para tiendas
    const handleDeleteTienda = async (id) => {
        const result = await Swal.fire({
            title: '¿Estás seguro?',
            text: 'No podrás revertir esto!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar!',
            cancelButtonText: 'Cancelar'
        });
        if (result.isConfirmed) {
            try {
                await del(`tiendas/${id}`);
                fetchData();
                Swal.fire('Eliminada!', 'La tienda ha sido eliminada.', 'success');
            } catch (error) {
                Swal.fire({
                    title: 'Error',
                    text: error.message,
                    icon: 'error',
                    confirmButtonText: 'Aceptar'
                });
            }
        }
    };

    const handleViewClientes = (tiendaId) => {
        navigate(`/tiendas/${tiendaId}/clientes`);
    };

    const handleCreateCliente = (tiendaId) => {
        navigate(`/tiendas/${tiendaId}/clientes/create`);
    };

    const handleShowWorkerFranchiseSelector = (tiendaId) => {
        setSelectedTiendaId(tiendaId);
        setShowWorkerFranchiseSelector(true);
    };

    const handleSelectWorker = () => {
        setShowTrabajadorModal(true);
    };

    const handleSelectFranchise = () => {
        setShowFranquiciadoModal(true);
    };

    const handleShowTrabajadorModal = (tiendaId) => {
        setSelectedTiendaId(tiendaId);
        setShowTrabajadorModal(true);
    };

    const handleShowQrModal = (tiendaId) => {
        setSelectedTiendaId(tiendaId);
        setShowQrModal(true);
    };

    const handleCreateStore = async (data) => {
        try {
            await post('tiendas', { ...data, usuario: user.userId });
            fetchData();
            setShowStoreModal(false);
            Swal.fire('Éxito', 'Tienda creada correctamente', 'success');
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    // Handlers para TPV
    const handleOpenTpvConfig = (tiendaId) => {
        setSelectedTiendaId(tiendaId);
        setShowTpvConfigModal(true);
    };

    const handleOpenTpvManager = (tiendaId) => {
        navigate(`/tiendas/${tiendaId}/tpv`);
    };

    const handleCreateTpvConfig = async (configData) => {
        try {
            await post(`tpv/tiendas/${selectedTiendaId}/configurations`, configData);
            await fetchData(); // Recargar datos incluyendo TPV
            setShowTpvConfigModal(false);
            Swal.fire({
                title: 'Éxito',
                text: 'Configuración TPV creada correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'Error al crear la configuración TPV',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    // Handlers para Embed
    const handleShowEmbedModal = () => {
        setShowEmbedModal(true);
    };

    const handleCreateEmbedConfig = async (configData) => {
        try {
            await post('embed/global/config', configData);
            await fetchData(); // Recargar datos incluyendo embed
            setShowEmbedModal(false);
            Swal.fire({
                title: 'Éxito',
                text: 'Configuración de embed creada correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'Error al crear la configuración de embed',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    const copyEmbedCode = async () => {
        try {
            // Generar código de embed temporal si no hay configuración
            let embedCode = 'TEMP_' + Date.now();
            let currentConfig = embedConfig;

            // Intentar obtener configuración existente
            if (currentConfig) {
                embedCode = currentConfig.embedCode || currentConfig.id || embedCode;
            } else {
                // Intentar crear configuración en el backend
                try {
                    const basicConfig = {
                        allowedDomains: [],
                        customStyles: {
                            primaryColor: '#3B82F6',
                            backgroundColor: '#FFFFFF',
                            textColor: '#1F2937',
                            borderRadius: '8px',
                            fontFamily: 'system-ui, sans-serif',
                            showHeader: true,
                            showBenefits: true
                        }
                    };

                    const newConfig = await post('embed/global/config', basicConfig);
                    currentConfig = newConfig.data || newConfig;
                    embedCode = currentConfig.embedCode || currentConfig.id || embedCode;
                    console.log('Configuración creada en backend:', currentConfig);
                } catch (backendError) {
                    console.warn('Backend no disponible, usando configuración temporal:', backendError);
                    // Usar configuración temporal
                    currentConfig = {
                        embedCode: embedCode,
                        id: embedCode,
                        customStyles: {
                            primaryColor: '#3B82F6',
                            backgroundColor: '#FFFFFF',
                            textColor: '#1F2937',
                            borderRadius: '8px',
                            fontFamily: 'system-ui, sans-serif',
                            showHeader: true,
                            showBenefits: true
                        }
                    };
                }
            }

            console.log('EmbedCode final:', embedCode);
            const baseUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;

            const scriptCode = `<script src="${baseUrl}/embed.js" 
        data-embed-code="${embedCode}"
        data-theme="modern">
</script>`;

            const iframeCode = `<iframe src="${baseUrl}/embed/global/${embedCode}" 
        width="100%" 
        height="600px"
        frameborder="0">
</iframe>`;

            // Mostrar modal con códigos
            Swal.fire({
                title: 'Código de Formulario Embebible',
                html: `
                    <div class="text-left">
                        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <h4 class="text-sm font-medium text-blue-800 mb-2">📋 Formulario Global</h4>
                            <p class="text-xs text-blue-600 mb-2">
                                Este formulario permite a los clientes seleccionar su tienda de consumo habitual 
                                y registrarse en tu sistema de fidelización.
                            </p>
                            <div class="text-xs text-gray-600">
                                <strong>Estado:</strong> ${currentConfig.embedCode ? 'Configuración del backend' : 'Configuración temporal'}
                                <br><strong>Código:</strong> ${embedCode}
                            </div>
                        </div>
                        
                        <h4 class="text-lg font-semibold mb-3">Código Script (Recomendado):</h4>
                        <div class="bg-gray-100 p-3 rounded mb-3">
                            <code class="text-sm break-all">${scriptCode}</code>
                        </div>
                        <button id="copyScript" class="bg-blue-500 text-white px-3 py-1 rounded text-sm mb-4">
                            Copiar Script
                        </button>
                        
                        <h4 class="text-lg font-semibold mb-3">Código Iframe:</h4>
                        <div class="bg-gray-100 p-3 rounded mb-3">
                            <code class="text-sm break-all">${iframeCode}</code>
                        </div>
                        <button id="copyIframe" class="bg-green-500 text-white px-3 py-1 rounded text-sm">
                            Copiar Iframe
                        </button>
                        
                        ${!currentConfig.embedCode ? `
                        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
                            <p class="text-xs text-yellow-800">
                                <strong>⚠️ Nota:</strong> Usando configuración temporal. Para funcionalidad completa, 
                                el backend debe implementar los endpoints de embed según la documentación.
                            </p>
                        </div>
                        ` : ''}
                    </div>
                `,
                width: '600px',
                showConfirmButton: false,
                showCancelButton: true,
                cancelButtonText: 'Cerrar',
                didOpen: () => {
                    // Event listeners para botones de copiar
                    document.getElementById('copyScript').addEventListener('click', () => {
                        navigator.clipboard.writeText(scriptCode).then(() => {
                            Swal.fire({
                                title: 'Copiado!',
                                text: 'Código script copiado al portapapeles',
                                icon: 'success',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        });
                    });

                    document.getElementById('copyIframe').addEventListener('click', () => {
                        navigator.clipboard.writeText(iframeCode).then(() => {
                            Swal.fire({
                                title: 'Copiado!',
                                text: 'Código iframe copiado al portapapeles',
                                icon: 'success',
                                timer: 2000,
                                showConfirmButton: false
                            });
                        });
                    });
                }
            });

        } catch (error) {
            console.error('Error completo:', error);
            Swal.fire({
                title: 'Error',
                html: `
                    <div class="text-left">
                        <p><strong>Error:</strong> ${error.message || 'Error al generar el código de embed'}</p>
                        <p><strong>Estado actual:</strong></p>
                        <ul class="text-sm text-gray-600 mt-2">
                            <li>• Configuración embed: ${embedConfig ? 'Existe' : 'No existe'}</li>
                            <li>• Usuario: ${user ? user.email : 'No autenticado'}</li>
                            <li>• Marcas: ${marcas.length}</li>
                            <li>• Tiendas: ${tiendas.length}</li>
                        </ul>
                        <p class="text-xs text-gray-500 mt-2">
                            <strong>Nota:</strong> El backend debe implementar los endpoints de embed según la documentación.
                        </p>
                    </div>
                `,
                width: '500px',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <FaSpinner className="animate-spin text-blue-500 text-4xl mx-auto mb-4" />
                    <p className="text-gray-600">Cargando gestión de marcas y tiendas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="container mx-auto px-4 py-6">

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Marcas y Tiendas</h1>
                    <p className="text-gray-600">
                        Administra tu negocio desde un solo lugar. Configura tus marcas con sus modelos de fidelización y gestiona todas tus tiendas con sus herramientas de captación de clientes.
                    </p>
                </div>



                {/* Contenido unificado de Marcas y Tiendas */}
                <div className="space-y-8">
                    {/* Sección de Marcas */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                                    <FaTags className="mr-2 text-blue-500" />
                                    Mis Marcas
                                </h2>
                                <p className="text-gray-600">Configuración de fidelización y gestión de marcas</p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowEmbedFormOptionsModal(true)}
                                    className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center"
                                    title="Añadir formulario en tu web"
                                    disabled={marcas.length === 0 || tiendas.length === 0}
                                >
                                    <FaGlobe className="mr-2" />
                                    Añadir Formulario en Web Propia
                                </button>
                                <button
                                    onClick={() => setShowMarcaModal(true)}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                                >
                                    <FaPlus className="mr-2" />
                                    Crear Marca
                                </button>
                            </div>
                        </div>

                        {marcas.length > 0 ? (
                            <div className="space-y-4">
                                {marcas.map((marca) => (
                                    <div key={marca._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h3 className="text-lg font-semibold text-gray-900">{marca.nombre}</h3>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleOpenEditMarca(marca)}
                                                            className="text-blue-500 hover:text-blue-700 p-1 transition-colors"
                                                            title="Editar marca"
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteMarca(marca._id)}
                                                            className="text-red-500 hover:text-red-700 p-1 transition-colors"
                                                            title="Eliminar marca"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-gray-500">Rubro:</span>
                                                        <p className="font-medium">{marca.rubro}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Tiendas:</span>
                                                        <p className="font-medium flex items-center">
                                                            <FaStore className="mr-1" />
                                                            {marca.tiendas.length}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Clientes:</span>
                                                        <p className="font-medium flex items-center">
                                                            <FaUsers className="mr-1" />
                                                            {marca.clientes.length}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Tipo:</span>
                                                        <p className="font-medium">{marca.puntos}</p>
                                                    </div>
                                                    {/* Redes Sociales */}
                                                    {marca.marketingInfo && Object.keys(marca.marketingInfo).some(key =>
                                                        ['website', 'facebook', 'instagram', 'twitter', 'linkedin', 'tiktok', 'youtube', 'whatsapp'].includes(key) && marca.marketingInfo[key]
                                                    ) && (
                                                            <div className="lg:col-span-4">
                                                                <span className="text-gray-500">Redes Sociales:</span>
                                                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                                    {marca.marketingInfo.website && (
                                                                        <a href={marca.marketingInfo.website} target="_blank" rel="noopener noreferrer"
                                                                            className="text-blue-600 hover:text-blue-800 transition-colors" title="Sitio Web">
                                                                            <FaGlobe className="text-lg" />
                                                                        </a>
                                                                    )}
                                                                    {marca.marketingInfo.facebook && (
                                                                        <a href={marca.marketingInfo.facebook} target="_blank" rel="noopener noreferrer"
                                                                            className="text-blue-600 hover:text-blue-800 transition-colors" title="Facebook">
                                                                            <FaFacebook className="text-lg" />
                                                                        </a>
                                                                    )}
                                                                    {marca.marketingInfo.instagram && (
                                                                        <a href={marca.marketingInfo.instagram} target="_blank" rel="noopener noreferrer"
                                                                            className="text-pink-600 hover:text-pink-800 transition-colors" title="Instagram">
                                                                            <FaInstagram className="text-lg" />
                                                                        </a>
                                                                    )}
                                                                    {marca.marketingInfo.twitter && (
                                                                        <a href={marca.marketingInfo.twitter} target="_blank" rel="noopener noreferrer"
                                                                            className="text-sky-600 hover:text-sky-800 transition-colors" title="Twitter/X">
                                                                            <FaTwitter className="text-lg" />
                                                                        </a>
                                                                    )}
                                                                    {marca.marketingInfo.linkedin && (
                                                                        <a href={marca.marketingInfo.linkedin} target="_blank" rel="noopener noreferrer"
                                                                            className="text-blue-700 hover:text-blue-900 transition-colors" title="LinkedIn">
                                                                            <FaLinkedin className="text-lg" />
                                                                        </a>
                                                                    )}
                                                                    {marca.marketingInfo.tiktok && (
                                                                        <a href={marca.marketingInfo.tiktok} target="_blank" rel="noopener noreferrer"
                                                                            className="text-gray-800 hover:text-gray-900 transition-colors" title="TikTok">
                                                                            <FaTiktok className="text-lg" />
                                                                        </a>
                                                                    )}
                                                                    {marca.marketingInfo.youtube && (
                                                                        <a href={marca.marketingInfo.youtube} target="_blank" rel="noopener noreferrer"
                                                                            className="text-red-600 hover:text-red-800 transition-colors" title="YouTube">
                                                                            <FaYoutube className="text-lg" />
                                                                        </a>
                                                                    )}
                                                                    {marca.marketingInfo.whatsapp && (
                                                                        <a href={`https://wa.me/${marca.marketingInfo.whatsapp}`} target="_blank" rel="noopener noreferrer"
                                                                            className="text-green-600 hover:text-green-800 transition-colors" title="WhatsApp">
                                                                            <FaWhatsapp className="text-lg" />
                                                                        </a>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                </div>

                                                {/* Palabras Clave y Hashtags */}
                                                {marca.marketingInfo && (marca.marketingInfo.palabrasClave?.length > 0 || marca.marketingInfo.hashtags?.length > 0) && (
                                                    <div className="mt-2">
                                                        {marca.marketingInfo.palabrasClave?.length > 0 && (
                                                            <div className="flex items-center gap-1 flex-wrap mb-1">
                                                                <span className="text-xs text-gray-500">SEO:</span>
                                                                {marca.marketingInfo.palabrasClave.slice(0, 3).map((palabra, idx) => (
                                                                    <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                                                        {palabra}
                                                                    </span>
                                                                ))}
                                                                {marca.marketingInfo.palabrasClave.length > 3 && (
                                                                    <span className="text-xs text-gray-400">+{marca.marketingInfo.palabrasClave.length - 3}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                        {marca.marketingInfo.hashtags?.length > 0 && (
                                                            <div className="flex items-center gap-1 flex-wrap">
                                                                <span className="text-xs text-gray-500">Hashtags:</span>
                                                                {marca.marketingInfo.hashtags.slice(0, 3).map((hashtag, idx) => (
                                                                    <span key={idx} className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                                                        {hashtag}
                                                                    </span>
                                                                ))}
                                                                {marca.marketingInfo.hashtags.length > 3 && (
                                                                    <span className="text-xs text-gray-400">+{marca.marketingInfo.hashtags.length - 3}</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="mt-3 pt-3 border-t border-gray-100">
                                                    <p className="text-sm text-gray-600">
                                                        <strong>Condición:</strong> {marca.pointscondition}
                                                    </p>
                                                    {marca.puntos === 'PUNTOS' && (
                                                        <p className="text-sm text-gray-600">
                                                            <strong>Puntos por ticket:</strong> {marca.puntosTicket}
                                                        </p>
                                                    )}

                                                    {/* Información del motor de puntos */}
                                                    {marca.pointsEngineVersion === 1 && marca.pointsRules && (
                                                        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                                            <div className="flex items-center gap-2">
                                                                <FaCog className="text-blue-600 text-sm" />
                                                                <span className="text-sm font-medium text-blue-800">
                                                                    Motor Automático Activo
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-blue-600 mt-1">
                                                                {marca.pointsRules.mode === 'RATE_PER_AMOUNT' &&
                                                                    `${marca.pointsRules.ratePerAmount?.rate || 1} punto(s) por cada ${marca.pointsRules.ratePerAmount?.step || 1}${getCurrencySymbol(marca.pointsRules?.currency || 'EUR')}`
                                                                }
                                                                {marca.pointsRules.mode === 'TIERED' &&
                                                                    'Sistema por niveles configurado'
                                                                }
                                                                {marca.pointsRules.mode === 'MULTIPLIER_BY_TAG' &&
                                                                    'Multiplicadores por categoría configurados'
                                                                }
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Nueva información de identidad visual */}
                                                {(marca.coloresCorporativos?.length > 0 || marca.tipografia || marca.eslogan) && (
                                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                                            <FaPalette className="mr-1" />
                                                            Identidad Visual
                                                        </h4>
                                                        <div className="space-y-2">
                                                            {marca.coloresCorporativos?.length > 0 && (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs text-gray-500">Colores:</span>
                                                                    <div className="flex gap-1">
                                                                        {marca.coloresCorporativos.map((color, index) => (
                                                                            <div
                                                                                key={index}
                                                                                className="w-4 h-4 rounded-full border border-gray-300"
                                                                                style={{ backgroundColor: color }}
                                                                                title={color}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {marca.tipografia && (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs text-gray-500">Tipografía:</span>
                                                                    <span className="text-xs font-medium">{marca.tipografia}</span>
                                                                </div>
                                                            )}
                                                            {marca.eslogan && (
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-xs text-gray-500">Eslogan:</span>
                                                                    <span className="text-xs font-medium italic">"{marca.eslogan}"</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="mt-3 flex gap-2 flex-wrap">
                                                    {marca.puntos === 'ACUMULACION' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleManagePremios(marca)}
                                                                className="bg-green-500 text-white px-3 py-1 rounded-lg hover:bg-green-600 transition-colors flex items-center text-sm"
                                                            >
                                                                <FaGift className="mr-1" />
                                                                Gestionar Premios
                                                            </button>
                                                            <button
                                                                onClick={() => handleConfigurePoints(marca)}
                                                                className="bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600 transition-colors flex items-center text-sm"
                                                            >
                                                                <FaCog className="mr-1" />
                                                                Configurar Puntos
                                                            </button>
                                                        </>
                                                    )}
                                                    {marca.puntos === 'PUNTOS' && (
                                                        <button
                                                            onClick={() => handleConfigurePoints(marca)}
                                                            className="bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600 transition-colors flex items-center text-sm"
                                                        >
                                                            <FaCog className="mr-1" />
                                                            Configurar Sellos
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => navigate(`/marcas/${marca._id}/personalizacion`)}
                                                        className="bg-purple-500 text-white px-3 py-1 rounded-lg hover:bg-purple-600 transition-colors flex items-center text-sm"
                                                    >
                                                        <FaPalette className="mr-1" />
                                                        Centro de Personalización
                                                    </button>
                                                    <button
                                                        onClick={() => handleManageMarketing(marca)}
                                                        className="bg-indigo-500 text-white px-3 py-1 rounded-lg hover:bg-indigo-600 transition-colors flex items-center text-sm"
                                                        title="Gestionar información de marketing y redes sociales"
                                                    >
                                                        <FaChartLine className="mr-1" />
                                                        Gestionar Marketing
                                                    </button>
                                                    <button
                                                        onClick={() => navigate(`/find-ticket/${marca._id}`)}
                                                        className="bg-orange-500 text-white px-3 py-1 rounded-lg hover:bg-orange-600 transition-colors flex items-center text-sm"
                                                        title="Página pública para que los clientes busquen su tarjeta de puntos"
                                                    >
                                                        <FaQrcode className="mr-1" />
                                                        Buscar Tarjeta (Pública)
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Logotipo a la derecha */}
                                            <div className="ml-6 flex-shrink-0">
                                                {marca.logotipo ? (
                                                    <div className="text-center">
                                                        <button
                                                            onClick={() => handleChangeLogotipo(marca)}
                                                            className="group relative"
                                                            title="Haz clic para cambiar el logotipo"
                                                        >
                                                            <img
                                                                src={marca.logotipo.cdnUrl || marca.logotipo.url}
                                                                alt={`Logotipo de ${marca.nombre}`}
                                                                className="w-24 h-24 object-contain border border-gray-200 rounded-lg bg-white group-hover:border-blue-400 group-hover:shadow-md transition-all cursor-pointer"
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'block';
                                                                }}
                                                            />
                                                            <div
                                                                className="w-24 h-24 object-contain border border-gray-200 rounded-lg bg-gray-100 items-center justify-center text-gray-400 text-xs"
                                                                style={{ display: 'none' }}
                                                            >
                                                                Error
                                                            </div>
                                                            {/* Overlay de hover */}
                                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all flex items-center justify-center">
                                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <FaImage className="text-white text-xl" />
                                                                </div>
                                                            </div>
                                                        </button>
                                                        <p className="text-xs text-gray-500 mt-1 truncate max-w-24">
                                                            {marca.logotipo.originalName}
                                                        </p>
                                                        <p className="text-xs text-blue-500 mt-1">
                                                            Clic para cambiar
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleChangeLogotipo(marca)}
                                                        className="w-24 h-24 bg-gray-100 rounded-lg flex flex-col items-center justify-center text-gray-400 text-xs hover:bg-gray-200 hover:border-2 hover:border-blue-300 transition-all cursor-pointer group"
                                                        title="Haz clic para agregar un logotipo"
                                                    >
                                                        <FaImage className="text-gray-400 group-hover:text-blue-500 mb-1 transition-colors" />
                                                        <span className="group-hover:text-blue-500 transition-colors">Sin logotipo</span>
                                                        <span className="text-xs text-blue-500 mt-1">Clic para agregar</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <FaTags className="text-3xl text-gray-300 mx-auto mb-3" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No tienes marcas creadas</h3>
                                <p className="text-gray-500 mb-4">Crea tu primera marca para comenzar a gestionar tus tiendas</p>
                                <button
                                    onClick={() => setShowMarcaModal(true)}
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Crear Primera Marca
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Sección de Tiendas */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex justify-between items-center mb-6">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                                    <FaStore className="mr-2 text-green-500" />
                                    Mis Tiendas
                                </h2>
                                <p className="text-gray-600">Gestión de tiendas y herramientas de captación de clientes</p>
                            </div>
                            <button
                                onClick={() => setShowStoreModal(true)}
                                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                            >
                                <FaPlus className="mr-2" />
                                Crear Tienda
                            </button>
                        </div>

                        {tiendas.length > 0 ? (
                            <div className="space-y-4">
                                {tiendas.map((tienda) => (
                                    <div key={tienda._id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h3 className="text-lg font-semibold text-gray-900">{tienda.nombre}</h3>
                                                    <button
                                                        onClick={() => handleDeleteTienda(tienda._id)}
                                                        className="text-red-500 hover:text-red-700 p-1 transition-colors"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>

                                                <p className="text-gray-600 mb-3">{tienda.descripcion}</p>

                                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm mb-4">
                                                    <div>
                                                        <span className="text-gray-500">Dirección:</span>
                                                        <p className="font-medium">
                                                            {tienda.via} {tienda.calle}, {tienda.numero}
                                                        </p>
                                                        <p className="text-gray-600">
                                                            {tienda.poblacion}, {tienda.pais}
                                                        </p>
                                                        <p className="text-gray-600">{tienda.region}</p>

                                                        {/* Estado TPV */}
                                                        {tpvConfigurations[tienda._id] && tpvConfigurations[tienda._id].length > 0 && (
                                                            <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                                                                <div className="flex items-center gap-2">
                                                                    <FaCreditCard className="text-green-600 text-sm" />
                                                                    <span className="text-sm font-medium text-green-800">
                                                                        TPV Configurado ({tpvConfigurations[tienda._id].length} terminal{tpvConfigurations[tienda._id].length > 1 ? 'es' : ''})
                                                                    </span>
                                                                </div>
                                                                {tpvStats[tienda._id] && (
                                                                    <p className="text-xs text-green-600 mt-1">
                                                                        {tpvStats[tienda._id].totalRequests} transacciones procesadas
                                                                    </p>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Indicador Google My Business */}
                                                        {tienda.googleMyBusiness && (
                                                            <div className="mt-2 p-2 bg-pink-50 border border-pink-200 rounded-lg">
                                                                <div className="flex items-center gap-2">
                                                                    <FaStar className="text-pink-600 text-sm" />
                                                                    <span className="text-sm font-medium text-pink-800">
                                                                        Google My Business Configurado
                                                                    </span>
                                                                </div>
                                                                <a
                                                                    href={tienda.googleMyBusiness}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="text-xs text-pink-600 hover:underline mt-1 block truncate"
                                                                >
                                                                    Ver en Google Maps →
                                                                </a>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="lg:col-span-2">
                                                        <span className="text-gray-500">Acciones:</span>
                                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
                                                            <button
                                                                className="bg-green-500 text-white text-sm px-3 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center"
                                                                onClick={() => handleViewClientes(tienda._id)}
                                                            >
                                                                <FaUsers className="mr-2" />
                                                                Ver Clientes
                                                            </button>
                                                            <button
                                                                className="bg-yellow-500 text-white text-sm px-3 py-2 rounded-lg hover:bg-yellow-600 transition-colors flex items-center justify-center"
                                                                onClick={() => handleCreateCliente(tienda._id)}
                                                            >
                                                                <FaUserPlus className="mr-2" />
                                                                Crear Cliente
                                                            </button>
                                                            <button
                                                                className="bg-blue-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                                                                onClick={() => handleShowWorkerFranchiseSelector(tienda._id)}
                                                            >
                                                                <FaUserPlus className="mr-2" />
                                                                Añadir Trabajador/Franquiciado
                                                            </button>
                                                            <button
                                                                className="bg-indigo-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center"
                                                                onClick={() => handleShowQrModal(tienda._id)}
                                                            >
                                                                <FaQrcode className="mr-2" />
                                                                QR/PDF
                                                            </button>
                                                            <button
                                                                className={`text-white text-sm px-3 py-2 rounded-lg transition-colors flex items-center justify-center ${tpvConfigurations[tienda._id] && tpvConfigurations[tienda._id].length > 0
                                                                    ? 'bg-purple-600 hover:bg-purple-700'
                                                                    : 'bg-gray-500 hover:bg-gray-600'
                                                                    }`}
                                                                onClick={() => handleOpenTpvManager(tienda._id)}
                                                                title={tpvConfigurations[tienda._id] && tpvConfigurations[tienda._id].length > 0 ? 'Gestionar TPV' : 'Configurar TPV'}
                                                            >
                                                                <FaCreditCard className="mr-2" />
                                                                TPV
                                                            </button>
                                                            <button
                                                                className="bg-pink-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-pink-700 transition-colors flex items-center justify-center"
                                                                onClick={() => handleManageGoogleMyBusiness(tienda._id)}
                                                                title={tienda.googleMyBusiness ? "Editar Google My Business" : "Añadir Google My Business"}
                                                            >
                                                                <FaStar className="mr-2" />
                                                                Google Reviews
                                                            </button>
                                                            <button
                                                                className="bg-teal-600 text-white text-sm px-3 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center"
                                                                onClick={() => handleManageMobileAppAccess(tienda._id)}
                                                                title="Gestionar accesos web para escanear QR y acumular puntos"
                                                            >
                                                                <FaGlobe className="mr-2" />
                                                                Accesos Web
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>


                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <FaStore className="text-3xl text-gray-300 mx-auto mb-3" />
                                <h3 className="text-lg font-medium text-gray-900 mb-2">No tienes tiendas creadas</h3>
                                <p className="text-gray-500 mb-4">Crea tu primera tienda para comenzar a gestionar clientes</p>
                                <button
                                    onClick={() => setShowStoreModal(true)}
                                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                                >
                                    Crear Primera Tienda
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modales */}

            {/* Modal Crear Marca */}
            <CreateMarcaModal
                isOpen={showMarcaModal}
                onClose={() => setShowMarcaModal(false)}
                onSubmit={handleCreateMarca}
                onComplete={() => setShowMarcaModal(false)}
                user={user}
            />

            {/* Modal Editar Marca */}
            <EditMarcaModal
                isOpen={showEditMarcaModal}
                onClose={() => setShowEditMarcaModal(false)}
                onSubmit={handleEditMarca}
                marca={selectedMarca}
                user={user}
            />

            {/* Otros modales */}
            {showModalPremio && (
                <ManagePremiosModal
                    marca={selectedMarca}
                    onClose={() => setShowModalPremio(false)}
                    onRefresh={fetchData}
                />
            )}

            {showStoreModal && (
                <CreateStoreModal
                    isOpen={showStoreModal}
                    onClose={() => setShowStoreModal(false)}
                    onSubmit={handleCreateStore}
                    marcas={marcas}
                />
            )}

            <WorkerFranchiseSelectorModal
                show={showWorkerFranchiseSelector}
                onClose={() => setShowWorkerFranchiseSelector(false)}
                onSelectWorker={handleSelectWorker}
                onSelectFranchise={handleSelectFranchise}
            />

            <TrabajadorModal
                show={showTrabajadorModal}
                onClose={() => setShowTrabajadorModal(false)}
                tiendaId={selectedTiendaId}
            />

            <FranquiciadoModal
                show={showFranquiciadoModal}
                onClose={() => setShowFranquiciadoModal(false)}
                tiendaId={selectedTiendaId}
            />

            {showQrModal && (
                <QrPdfModal
                    tiendaId={selectedTiendaId}
                    onClose={() => setShowQrModal(false)}
                />
            )}

            {/* Modal para cambiar logotipo */}
            {showLogotipoModal && marcaForLogotipo && (
                <LogotipoModal
                    isOpen={showLogotipoModal}
                    onClose={() => {
                        setShowLogotipoModal(false);
                        setMarcaForLogotipo(null);
                    }}
                    onUploadLogotipo={handleUploadLogotipo}
                    marca={marcaForLogotipo}
                    title="Cambiar Logotipo"
                    isEdit={true}
                />
            )}

            {/* Modal para logotipo de marca nueva */}
            {showNewMarcaLogotipoModal && newMarcaCreated && (
                <LogotipoModal
                    isOpen={showNewMarcaLogotipoModal}
                    onClose={() => {
                        setShowNewMarcaLogotipoModal(false);
                        setNewMarcaCreated(null);
                    }}
                    onUploadLogotipo={handleUploadLogotipo}
                    marca={newMarcaCreated}
                    title="Agregar Logotipo a Nueva Marca"
                    isEdit={false}
                />
            )}

            {/* Modal de configuración TPV */}
            {showTpvConfigModal && (
                <TpvConfigModal
                    isOpen={showTpvConfigModal}
                    onClose={() => setShowTpvConfigModal(false)}
                    onSubmit={handleCreateTpvConfig}
                    tiendaId={selectedTiendaId}
                />
            )}

            {/* Modal de configuración Embed */}
            {showEmbedModal && (
                <EmbedConfigModal
                    isOpen={showEmbedModal}
                    onClose={() => setShowEmbedModal(false)}
                    onSubmit={handleCreateEmbedConfig}
                    tiendas={tiendas}
                />
            )}

            {/* Modal de configuración de puntos */}
            {showPointsConfigModal && marcaForPointsConfig && (
                marcaForPointsConfig.puntos === 'PUNTOS' ? (
                    <PointsStampsConfigModal
                        isOpen={showPointsConfigModal}
                        onClose={() => {
                            setShowPointsConfigModal(false);
                            setMarcaForPointsConfig(null);
                        }}
                        onSubmit={handleSavePointsConfig}
                        marca={marcaForPointsConfig}
                    />
                ) : (
                    <PointsRulesConfigModal
                        isOpen={showPointsConfigModal}
                        onClose={() => {
                            setShowPointsConfigModal(false);
                            setMarcaForPointsConfig(null);
                        }}
                        onSubmit={handleSavePointsConfig}
                        marca={marcaForPointsConfig}
                    />
                )
            )}

            {/* Modal de opciones de formulario embebible */}
            {showEmbedFormOptionsModal && marcas.length > 0 && (
                <EmbedFormOptionsModal
                    isOpen={showEmbedFormOptionsModal}
                    onClose={() => setShowEmbedFormOptionsModal(false)}
                    marca={marcas[0]}
                    tiendas={tiendas}
                />
            )}

            {/* Modal de Marketing Info */}
            {showMarketingModal && marcaForMarketing && (
                <MarketingInfoModal
                    isOpen={showMarketingModal}
                    onClose={() => {
                        setShowMarketingModal(false);
                        setMarcaForMarketing(null);
                    }}
                    marca={marcaForMarketing}
                    onSubmit={handleSaveMarketingInfo}
                />
            )}

            {/* Modal de Google My Business */}
            {showGoogleMyBusinessModal && tiendaForGMB && (
                <GoogleMyBusinessModal
                    isOpen={showGoogleMyBusinessModal}
                    onClose={() => {
                        setShowGoogleMyBusinessModal(false);
                        setTiendaForGMB(null);
                    }}
                    tienda={tiendaForGMB}
                    onSubmit={handleSaveGoogleMyBusiness}
                />
            )}

            {/* Modal de Accesos Móviles */}
            {showMobileAppAccessModal && tiendaForMobileAccess && (
                <MobileAppAccessModal
                    isOpen={showMobileAppAccessModal}
                    onClose={() => {
                        setShowMobileAppAccessModal(false);
                        setTiendaForMobileAccess(null);
                    }}
                    tiendaId={tiendaForMobileAccess._id}
                    tiendaNombre={tiendaForMobileAccess.nombre}
                />
            )}
        </div>
    );
};

// Modal de Marketing Info
const MarketingInfoModal = ({ isOpen, onClose, marca, onSubmit }) => {
    const [formData, setFormData] = useState({
        website: '',
        facebook: '',
        instagram: '',
        twitter: '',
        linkedin: '',
        tiktok: '',
        youtube: '',
        whatsapp: '',
        direccion: '',
        horarios: '',
        palabrasClave: [],
        hashtags: []
    });
    const [newKeyword, setNewKeyword] = useState('');
    const [newHashtag, setNewHashtag] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Función helper para normalizar URLs
    const normalizeUrl = (url) => {
        if (!url || url.trim() === '') return '';

        let normalized = url.trim();

        // Si ya tiene https:// correcto, devolverlo
        if (normalized.startsWith('https://')) {
            return normalized;
        }

        // Convertir http:// a https://
        if (normalized.startsWith('http://')) {
            return normalized.replace('http://', 'https://');
        }

        // Si tiene :// pero no el protocolo completo (ej: "://www.ejemplo.com")
        if (normalized.startsWith('://')) {
            return 'https' + normalized;
        }

        // Si tiene www. o empieza con un dominio, añadir https://
        if (normalized.startsWith('www.') || /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}/.test(normalized)) {
            return 'https://' + normalized;
        }

        // Si parece una URL pero le faltan las //
        if (normalized.startsWith('https:') && !normalized.startsWith('https://')) {
            return normalized.replace('https:', 'https://');
        }

        if (normalized.startsWith('http:') && !normalized.startsWith('http://')) {
            return normalized.replace('http:', 'https://');
        }

        // Si no parece una URL válida, añadir https:// al principio
        return 'https://' + normalized;
    };

    // Handler para cambios en campos de URL con normalización
    const handleUrlChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    // Normalizar URL cuando el usuario sale del campo (onBlur)
    const handleUrlBlur = (field, value) => {
        if (value && value.trim() !== '') {
            const normalized = normalizeUrl(value);
            setFormData({ ...formData, [field]: normalized });
        }
    };

    useEffect(() => {
        if (marca?.marketingInfo) {
            setFormData({
                website: marca.marketingInfo.website || '',
                facebook: marca.marketingInfo.facebook || '',
                instagram: marca.marketingInfo.instagram || '',
                twitter: marca.marketingInfo.twitter || '',
                linkedin: marca.marketingInfo.linkedin || '',
                tiktok: marca.marketingInfo.tiktok || '',
                youtube: marca.marketingInfo.youtube || '',
                whatsapp: marca.marketingInfo.whatsapp || '',
                direccion: marca.marketingInfo.direccion || '',
                horarios: marca.marketingInfo.horarios || '',
                palabrasClave: marca.marketingInfo.palabrasClave || [],
                hashtags: marca.marketingInfo.hashtags || []
            });
        }
    }, [marca]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await onSubmit({ marketingInfo: formData }, marca._id);
            onClose();
        } catch (error) {
            console.error('Error updating marketing info:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const addKeyword = () => {
        if (newKeyword.trim() && !formData.palabrasClave.includes(newKeyword.trim())) {
            setFormData({
                ...formData,
                palabrasClave: [...formData.palabrasClave, newKeyword.trim()]
            });
            setNewKeyword('');
        }
    };

    const removeKeyword = (index) => {
        setFormData({
            ...formData,
            palabrasClave: formData.palabrasClave.filter((_, i) => i !== index)
        });
    };

    const addHashtag = () => {
        if (newHashtag.trim()) {
            const hashtag = newHashtag.trim().startsWith('#') ? newHashtag.trim() : `#${newHashtag.trim()}`;
            if (!formData.hashtags.includes(hashtag)) {
                setFormData({
                    ...formData,
                    hashtags: [...formData.hashtags, hashtag]
                });
                setNewHashtag('');
            }
        }
    };

    const removeHashtag = (index) => {
        setFormData({
            ...formData,
            hashtags: formData.hashtags.filter((_, i) => i !== index)
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4">
                <div className="fixed inset-0 transition-opacity" onClick={onClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-4xl mx-auto relative z-10">
                    <div className="px-6 pt-6 pb-4 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                                <FaChartLine className="w-5 h-5 mr-2 text-indigo-500" />
                                Información de Marketing - {marca?.nombre}
                            </h3>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                            {/* Sitio Web y WhatsApp */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                        <FaGlobe className="mr-2 text-blue-500" />
                                        Sitio Web
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.website}
                                        onChange={(e) => handleUrlChange('website', e.target.value)}
                                        onBlur={(e) => handleUrlBlur('website', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="tusitio.com o www.tusitio.com"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Se añadirá https:// automáticamente si falta</p>
                                </div>
                                <div>
                                    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                        <FaWhatsapp className="mr-2 text-green-500" />
                                        WhatsApp Business
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.whatsapp}
                                        onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        placeholder="34612345678 (sin + ni espacios)"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Formato: código país + número (ej: 34612345678)</p>
                                </div>
                            </div>

                            {/* Redes Sociales */}
                            <div className="border-t border-gray-200 pt-4">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                    <FaUsers className="mr-2 text-indigo-500" />
                                    Redes Sociales
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                            <FaFacebook className="mr-2 text-blue-600" />
                                            Facebook
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.facebook}
                                            onChange={(e) => handleUrlChange('facebook', e.target.value)}
                                            onBlur={(e) => handleUrlBlur('facebook', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="facebook.com/tupagina"
                                        />
                                    </div>
                                    <div>
                                        <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                            <FaInstagram className="mr-2 text-pink-600" />
                                            Instagram
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.instagram}
                                            onChange={(e) => handleUrlChange('instagram', e.target.value)}
                                            onBlur={(e) => handleUrlBlur('instagram', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="instagram.com/tuperfil"
                                        />
                                    </div>
                                    <div>
                                        <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                            <FaTwitter className="mr-2 text-sky-500" />
                                            Twitter / X
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.twitter}
                                            onChange={(e) => handleUrlChange('twitter', e.target.value)}
                                            onBlur={(e) => handleUrlBlur('twitter', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="twitter.com/tuperfil"
                                        />
                                    </div>
                                    <div>
                                        <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                            <FaLinkedin className="mr-2 text-blue-700" />
                                            LinkedIn
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.linkedin}
                                            onChange={(e) => handleUrlChange('linkedin', e.target.value)}
                                            onBlur={(e) => handleUrlBlur('linkedin', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="linkedin.com/company/tuempresa"
                                        />
                                    </div>
                                    <div>
                                        <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                            <FaTiktok className="mr-2 text-gray-800" />
                                            TikTok
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.tiktok}
                                            onChange={(e) => handleUrlChange('tiktok', e.target.value)}
                                            onBlur={(e) => handleUrlBlur('tiktok', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="tiktok.com/@tuperfil"
                                        />
                                    </div>
                                    <div>
                                        <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                                            <FaYoutube className="mr-2 text-red-600" />
                                            YouTube
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.youtube}
                                            onChange={(e) => handleUrlChange('youtube', e.target.value)}
                                            onBlur={(e) => handleUrlBlur('youtube', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="youtube.com/@tucanal"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Información Corporativa */}
                            <div className="border-t border-gray-200 pt-4">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                    <FaBuilding className="mr-2 text-indigo-500" />
                                    Información Corporativa
                                </h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Dirección Corporativa
                                        </label>
                                        <textarea
                                            value={formData.direccion}
                                            onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="Calle Principal 123, 28001 Madrid, España"
                                            rows={2}
                                            maxLength={500}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Horarios
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.horarios}
                                            onChange={(e) => setFormData({ ...formData, horarios: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="Lunes a Viernes: 9:00 - 18:00"
                                            maxLength={200}
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Máximo 200 caracteres</p>
                                    </div>
                                </div>
                            </div>

                            {/* SEO y Marketing */}
                            <div className="border-t border-gray-200 pt-4">
                                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                    <FaSearch className="mr-2 text-indigo-500" />
                                    SEO y Marketing
                                </h4>

                                {/* Palabras Clave */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Palabras Clave (SEO)
                                    </label>
                                    <div className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={newKeyword}
                                            onChange={(e) => setNewKeyword(e.target.value)}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="Añadir palabra clave"
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                                        />
                                        <button
                                            type="button"
                                            onClick={addKeyword}
                                            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                        >
                                            <FaPlus />
                                        </button>
                                    </div>
                                    {formData.palabrasClave.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {formData.palabrasClave.map((palabra, index) => (
                                                <span
                                                    key={index}
                                                    className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                                                >
                                                    {palabra}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeKeyword(index)}
                                                        className="text-blue-600 hover:text-blue-800 ml-1"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Hashtags */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Hashtags
                                    </label>
                                    <div className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={newHashtag}
                                            onChange={(e) => setNewHashtag(e.target.value)}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                            placeholder="Añadir hashtag (con o sin #)"
                                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
                                        />
                                        <button
                                            type="button"
                                            onClick={addHashtag}
                                            className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
                                        >
                                            <FaPlus />
                                        </button>
                                    </div>
                                    {formData.hashtags.length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {formData.hashtags.map((hashtag, index) => (
                                                <span
                                                    key={index}
                                                    className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm"
                                                >
                                                    {hashtag}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeHashtag(index)}
                                                        className="text-purple-600 hover:text-purple-800 ml-1"
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Información */}
                            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <FaInfoCircle className="text-indigo-500 mt-1 mr-2 flex-shrink-0" />
                                    <div className="text-xs text-indigo-600">
                                        <p className="mb-1">Esta información se aplicará a toda la marca y podrá ser utilizada en:</p>
                                        <ul className="list-disc list-inside space-y-1">
                                            <li>Páginas públicas de clientes</li>
                                            <li>Emails y comunicaciones</li>
                                            <li>Materiales de marketing</li>
                                            <li>SEO y posicionamiento en buscadores</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Botones */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 font-medium flex items-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave className="w-4 h-4 mr-2" />
                                            Guardar Información
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Modal de Google My Business
const GoogleMyBusinessModal = ({ isOpen, onClose, tienda, onSubmit }) => {
    const [googleMyBusinessUrl, setGoogleMyBusinessUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (tienda?.googleMyBusiness) {
            setGoogleMyBusinessUrl(tienda.googleMyBusiness);
        } else {
            setGoogleMyBusinessUrl('');
        }
    }, [tienda]);

    // Función helper para normalizar URLs
    const normalizeUrl = (url) => {
        if (!url || url.trim() === '') return '';

        let normalized = url.trim();

        // Si ya tiene https:// correcto, devolverlo
        if (normalized.startsWith('https://')) {
            return normalized;
        }

        // Convertir http:// a https://
        if (normalized.startsWith('http://')) {
            return normalized.replace('http://', 'https://');
        }

        // Si tiene :// pero no el protocolo completo
        if (normalized.startsWith('://')) {
            return 'https' + normalized;
        }

        // Si parece una URL de g.page
        if (normalized.startsWith('g.page')) {
            return 'https://' + normalized;
        }

        // Si tiene www. o empieza con un dominio
        if (normalized.startsWith('www.') || /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}/.test(normalized)) {
            return 'https://' + normalized;
        }

        // Si parece una URL pero le faltan las //
        if (normalized.startsWith('https:') && !normalized.startsWith('https://')) {
            return normalized.replace('https:', 'https://');
        }

        if (normalized.startsWith('http:') && !normalized.startsWith('http://')) {
            return normalized.replace('http:', 'https://');
        }

        // Si no parece una URL válida, añadir https://
        return 'https://' + normalized;
    };

    // Normalizar URL cuando el usuario sale del campo
    const handleUrlBlur = () => {
        if (googleMyBusinessUrl && googleMyBusinessUrl.trim() !== '') {
            const normalized = normalizeUrl(googleMyBusinessUrl);
            setGoogleMyBusinessUrl(normalized);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await onSubmit({ googleMyBusiness: googleMyBusinessUrl }, tienda._id);
            Swal.fire({
                title: 'Éxito',
                text: 'Google My Business actualizado correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });
            onClose();
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message || 'Error al actualizar Google My Business',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4">
                <div className="fixed inset-0 transition-opacity" onClick={onClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-md mx-auto relative z-10">
                    <div className="px-6 pt-6 pb-4">
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <FaStar className="w-5 h-5 mr-2 text-pink-500" />
                                Google My Business - {tienda?.nombre}
                            </h3>
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Enlace para Pedir Reseñas
                                </label>
                                <input
                                    type="text"
                                    value={googleMyBusinessUrl}
                                    onChange={(e) => setGoogleMyBusinessUrl(e.target.value)}
                                    onBlur={handleUrlBlur}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    placeholder="g.page/r/TuCodigoAqui/review"
                                    required
                                />
                                <p className="text-xs text-gray-400 mt-1">
                                    Se añadirá https:// automáticamente si falta
                                </p>
                            </div>

                            {/* Instrucciones */}
                            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                                <h4 className="text-sm font-medium text-pink-800 mb-2 flex items-center">
                                    <FaInfoCircle className="mr-2" />
                                    ¿Cómo obtener el enlace?
                                </h4>
                                <ol className="text-xs text-pink-700 space-y-2 list-decimal list-inside">
                                    <li>
                                        Entra en <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-pink-900">Google Business Profile</a>
                                    </li>
                                    <li>Haz clic en el botón <strong>"Ver tu perfil"</strong></li>
                                    <li>En la nueva página, haz clic en el botón <strong>"Pedir una reseña"</strong></li>
                                    <li>Copia el enlace del modal que aparece (formato: <code className="text-xs bg-pink-100 px-1 rounded">https://g.page/r/...</code>)</li>
                                    <li>Pégalo en el campo de arriba</li>
                                </ol>
                            </div>

                            {/* Botones */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:bg-pink-400 font-medium flex items-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                            Guardando...
                                        </>
                                    ) : (
                                        <>
                                            <FaSave className="w-4 h-4 mr-2" />
                                            Guardar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Modal para cambiar logotipo
const LogotipoChangeModal = ({ marca, onClose, onUploadLogotipo }) => {
    const [logotipoFile, setLogotipoFile] = useState(null);
    const [logotipoPreview, setLogotipoPreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleLogotipoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validar tipo de archivo
        if (!file.type.startsWith('image/')) {
            Swal.fire({
                title: 'Error',
                text: 'Solo se permiten archivos de imagen (JPEG, PNG, GIF, WebP, SVG)',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Validar extensiones específicas
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
        const fileExtension = file.name.split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
            Swal.fire({
                title: 'Error',
                text: 'Formato de archivo no soportado. Use: JPG, PNG, GIF, WebP o SVG',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        // Validar tamaño (5MB máximo)
        if (file.size > 5 * 1024 * 1024) {
            Swal.fire({
                title: 'Error',
                text: 'El archivo es demasiado grande. Máximo 5MB',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        setLogotipoFile(file);

        // Crear preview
        const reader = new FileReader();
        reader.onload = (e) => {
            setLogotipoPreview(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async () => {
        if (!logotipoFile) {
            Swal.fire({
                title: 'Error',
                text: 'Por favor selecciona un archivo de logotipo',
                icon: 'error',
                confirmButtonText: 'Aceptar'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            await onUploadLogotipo(marca._id, logotipoFile);

            Swal.fire({
                title: '¡Logotipo Actualizado!',
                text: 'El logotipo se ha cambiado exitosamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });

            onClose();
        } catch (error) {
            console.error('Error subiendo logotipo:', error);

            Swal.fire({
                title: 'Error',
                text: 'No se pudo cambiar el logotipo. Por favor, intenta de nuevo.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Contenedor del modal */}
                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-md mx-auto">
                    <div className="px-6 pt-6 pb-4">
                        {/* Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <FaImage className="w-5 h-5 mr-2 text-blue-500" />
                                Cambiar Logotipo
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Contenido */}
                        <div className="mt-6 space-y-4">
                            {/* Logotipo actual */}
                            {marca.logotipo && (
                                <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <p className="text-sm text-gray-600 mb-2">Logotipo actual:</p>
                                    <img
                                        src={marca.logotipo.cdnUrl || marca.logotipo.url}
                                        alt={`Logotipo actual de ${marca.nombre}`}
                                        className="w-20 h-20 object-contain border border-gray-200 rounded-lg bg-white mx-auto"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{marca.logotipo.originalName}</p>
                                </div>
                            )}

                            {/* Área de subida */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nuevo logotipo
                                </label>
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleLogotipoChange}
                                        className="hidden"
                                        id="logotipo-change-upload"
                                    />
                                    <label htmlFor="logotipo-change-upload" className="cursor-pointer">
                                        {logotipoPreview ? (
                                            <div className="space-y-3">
                                                <img
                                                    src={logotipoPreview}
                                                    alt="Nuevo logotipo"
                                                    className="max-w-24 h-auto rounded-lg mx-auto border border-gray-200"
                                                />
                                                <p className="text-sm text-gray-600">
                                                    {logotipoFile?.name}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setLogotipoFile(null);
                                                        setLogotipoPreview(null);
                                                    }}
                                                    className="text-red-500 hover:text-red-700 text-sm"
                                                >
                                                    Cambiar archivo
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                <FaUpload className="mx-auto h-10 w-10 text-gray-400" />
                                                <div className="text-sm text-gray-600">
                                                    <span className="font-medium text-blue-600 hover:text-blue-500">
                                                        Haz clic para seleccionar
                                                    </span>
                                                    <p>o arrastra y suelta</p>
                                                </div>
                                                <p className="text-xs text-gray-500">
                                                    Formatos: JPG, PNG, GIF, WebP, SVG. Máx: 5MB
                                                </p>
                                            </div>
                                        )}
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Botones */}
                        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={!logotipoFile || isSubmitting}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center"
                            >
                                {isSubmitting ? (
                                    <>
                                        <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                        Cambiando...
                                    </>
                                ) : (
                                    <>
                                        <FaImage className="w-4 h-4 mr-2" />
                                        Cambiar Logotipo
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Función de fallback usando XMLHttpRequest
const uploadWithXHR = (url, formData, token) => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open('POST', url, true);

        // Configurar headers
        if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
        // Solo headers esenciales para evitar problemas de CORS
        // NO establecer Content-Type, dejar que se establezca automáticamente

        xhr.onload = function () {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response);
                } catch (e) {
                    resolve(xhr.responseText);
                }
            } else {
                reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
            }
        };

        xhr.onerror = function () {
            reject(new Error('Network error'));
        };

        xhr.upload.onprogress = function (e) {
            // Progreso de subida silencioso para producción
        };

        xhr.send(formData);
    });
};

// Modal de configuración TPV
const TpvConfigModal = ({ isOpen, onClose, onSubmit, tiendaId }) => {
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        tipoSoftware: 'agora',
        ipAddress: '',
        agoraStoreId: '',
        allowedIPs: [],
        enableReconciliation: true,
        notificationEmails: []
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newIp, setNewIp] = useState('');
    const [newEmail, setNewEmail] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Preparar datos para enviar: omitir ipAddress si está vacío
            const dataToSend = { ...formData };
            if (!dataToSend.ipAddress || dataToSend.ipAddress.trim() === '') {
                delete dataToSend.ipAddress;
            }
            await onSubmit(dataToSend);
        } catch (error) {
            console.error('Error creating TPV config:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({
            nombre: '',
            descripcion: '',
            tipoSoftware: 'agora',
            ipAddress: '',
            agoraStoreId: '',
            allowedIPs: [],
            enableReconciliation: true,
            notificationEmails: []
        });
        setNewIp('');
        setNewEmail('');
        onClose();
    };

    const addIp = () => {
        if (newIp.trim()) {
            setFormData({
                ...formData,
                allowedIPs: [...formData.allowedIPs, newIp.trim()]
            });
            setNewIp('');
        }
    };

    const removeIp = (index) => {
        setFormData({
            ...formData,
            allowedIPs: formData.allowedIPs.filter((_, i) => i !== index)
        });
    };

    const addEmail = () => {
        if (newEmail.trim()) {
            setFormData({
                ...formData,
                notificationEmails: [...formData.notificationEmails, newEmail.trim()]
            });
            setNewEmail('');
        }
    };

    const removeEmail = (index) => {
        setFormData({
            ...formData,
            notificationEmails: formData.notificationEmails.filter((_, i) => i !== index)
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={handleClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Contenedor del modal */}
                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-lg mx-auto">
                    <div className="px-6 pt-6 pb-4">
                        {/* Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <FaCreditCard className="w-5 h-5 mr-2 text-purple-500" />
                                Configurar TPV
                            </h3>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Contenido */}
                        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nombre del TPV *
                                </label>
                                <input
                                    type="text"
                                    value={formData.nombre}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="Ej: TPV Principal - Caja 1"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Descripción
                                </label>
                                <textarea
                                    value={formData.descripcion}
                                    onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="Descripción opcional del terminal"
                                    rows={2}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tipo de Software *
                                </label>
                                <select
                                    value={formData.tipoSoftware}
                                    onChange={(e) => setFormData({ ...formData, tipoSoftware: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    required
                                >
                                    <option value="agora">Agora Software</option>
                                    <option value="otro">Otro</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Dirección IP (Opcional)
                                </label>
                                <input
                                    type="text"
                                    value={formData.ipAddress}
                                    onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="192.168.1.100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Agora Store ID (Opcional)
                                </label>
                                <input
                                    type="text"
                                    value={formData.agoraStoreId}
                                    onChange={(e) => setFormData({ ...formData, agoraStoreId: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="AGORA_STORE_001"
                                />
                                <p className="text-xs text-gray-500 mt-1">ID de la tienda en el sistema de Agora</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    IPs Permitidas (Opcional)
                                </label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={newIp}
                                        onChange={(e) => setNewIp(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        placeholder="192.168.1.100"
                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addIp())}
                                    />
                                    <button
                                        type="button"
                                        onClick={addIp}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                                    >
                                        Añadir
                                    </button>
                                </div>
                                {formData.allowedIPs.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {formData.allowedIPs.map((ip, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-sm"
                                            >
                                                {ip}
                                                <button
                                                    type="button"
                                                    onClick={() => removeIp(index)}
                                                    className="text-purple-600 hover:text-purple-800 ml-1"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">Lista de IPs autorizadas para este TPV</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Emails de Notificación (Opcional)
                                </label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="email"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                        placeholder="admin@petitprime.com"
                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
                                    />
                                    <button
                                        type="button"
                                        onClick={addEmail}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                                    >
                                        Añadir
                                    </button>
                                </div>
                                {formData.notificationEmails.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {formData.notificationEmails.map((email, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm"
                                            >
                                                {email}
                                                <button
                                                    type="button"
                                                    onClick={() => removeEmail(index)}
                                                    className="text-blue-600 hover:text-blue-800 ml-1"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">Emails para recibir notificaciones del TPV</p>
                            </div>

                            <div>
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={formData.enableReconciliation}
                                        onChange={(e) => setFormData({ ...formData, enableReconciliation: e.target.checked })}
                                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Habilitar Conciliación</span>
                                </label>
                                <p className="text-xs text-gray-500 mt-1">Permitir conciliación de transacciones</p>
                            </div>

                            {/* Información adicional */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <FaCog className="text-blue-500 mt-1 mr-2 flex-shrink-0" />
                                    <div>
                                        <h4 className="text-sm font-medium text-blue-800 mb-1">Configuración en Agora</h4>
                                        <p className="text-xs text-blue-600 mb-2">
                                            Después de crear la configuración, recibirás una API Key que deberás configurar en tu software Agora.
                                        </p>
                                        <p className="text-xs text-blue-600">
                                            <strong>Webhook URL:</strong> {process.env.REACT_APP_BASE_API_URL || 'http://localhost:3000/api'}/api/webhooks/agora
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Botones */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                            Creando...
                                        </>
                                    ) : (
                                        <>
                                            <FaCreditCard className="w-4 h-4 mr-2" />
                                            Crear Configuración
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Modal de configuración Embed
const EmbedConfigModal = ({ isOpen, onClose, onSubmit, tiendas }) => {
    const [formData, setFormData] = useState({
        allowedDomains: [],
        customStyles: {
            primaryColor: '#3B82F6',
            backgroundColor: '#FFFFFF',
            textColor: '#1F2937',
            borderRadius: '8px',
            fontFamily: 'system-ui, sans-serif',
            showHeader: true,
            showBenefits: true
        }
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newDomain, setNewDomain] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            await onSubmit(formData);
        } catch (error) {
            console.error('Error creating embed config:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({
            allowedDomains: [],
            customStyles: {
                primaryColor: '#3B82F6',
                backgroundColor: '#FFFFFF',
                textColor: '#1F2937',
                borderRadius: '8px',
                fontFamily: 'system-ui, sans-serif',
                showHeader: true,
                showBenefits: true
            }
        });
        setNewDomain('');
        onClose();
    };

    const addDomain = () => {
        if (newDomain.trim()) {
            setFormData({
                ...formData,
                allowedDomains: [...formData.allowedDomains, newDomain.trim()]
            });
            setNewDomain('');
        }
    };

    const removeDomain = (index) => {
        setFormData({
            ...formData,
            allowedDomains: formData.allowedDomains.filter((_, i) => i !== index)
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
                {/* Fondo oscuro */}
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={handleClose}>
                    <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                </div>

                {/* Contenedor del modal */}
                <div className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all w-full max-w-2xl mx-auto">
                    <div className="px-6 pt-6 pb-4">
                        {/* Header */}
                        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                <FaCode className="w-5 h-5 mr-2 text-orange-500" />
                                Configurar Formulario Embebible
                            </h3>
                            <button
                                onClick={handleClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Contenido */}
                        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                            {/* Información del formulario global */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <FaCode className="text-blue-500 mt-1 mr-2 flex-shrink-0" />
                                    <div>
                                        <h4 className="text-sm font-medium text-blue-800 mb-1">Formulario Global de Registro</h4>
                                        <p className="text-xs text-blue-600 mb-2">
                                            Este formulario permite a los clientes seleccionar su tienda de consumo habitual
                                            y registrarse en tu sistema de fidelización desde cualquier sitio web.
                                        </p>
                                        <div className="text-xs text-blue-600">
                                            <strong>Tiendas disponibles:</strong> {tiendas.length} tienda{tiendas.length !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Dominios permitidos */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Dominios Permitidos (Opcional)
                                </label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={newDomain}
                                        onChange={(e) => setNewDomain(e.target.value)}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        placeholder="midominio.com"
                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
                                    />
                                    <button
                                        type="button"
                                        onClick={addDomain}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                                    >
                                        Añadir
                                    </button>
                                </div>
                                {formData.allowedDomains.length > 0 && (
                                    <div className="flex flex-wrap gap-2">
                                        {formData.allowedDomains.map((domain, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center gap-1 bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-sm"
                                            >
                                                {domain}
                                                <button
                                                    type="button"
                                                    onClick={() => removeDomain(index)}
                                                    className="text-orange-600 hover:text-orange-800 ml-1"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <p className="text-xs text-gray-500 mt-1">Deja vacío para permitir cualquier dominio</p>
                            </div>

                            {/* Estilos personalizados */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Color Primario
                                    </label>
                                    <input
                                        type="color"
                                        value={formData.customStyles.primaryColor}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            customStyles: { ...formData.customStyles, primaryColor: e.target.value }
                                        })}
                                        className="w-full h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Color de Fondo
                                    </label>
                                    <input
                                        type="color"
                                        value={formData.customStyles.backgroundColor}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            customStyles: { ...formData.customStyles, backgroundColor: e.target.value }
                                        })}
                                        className="w-full h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Color del Texto
                                    </label>
                                    <input
                                        type="color"
                                        value={formData.customStyles.textColor}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            customStyles: { ...formData.customStyles, textColor: e.target.value }
                                        })}
                                        className="w-full h-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Radio de Bordes
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.customStyles.borderRadius}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            customStyles: { ...formData.customStyles, borderRadius: e.target.value }
                                        })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        placeholder="8px"
                                    />
                                </div>
                            </div>

                            {/* Opciones de visualización */}
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-gray-700">Opciones de Visualización</h4>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="showHeader"
                                        checked={formData.customStyles.showHeader}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            customStyles: { ...formData.customStyles, showHeader: e.target.checked }
                                        })}
                                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                    />
                                    <label htmlFor="showHeader" className="text-sm text-gray-700">
                                        Mostrar header con información de la tienda
                                    </label>
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="showBenefits"
                                        checked={formData.customStyles.showBenefits}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            customStyles: { ...formData.customStyles, showBenefits: e.target.checked }
                                        })}
                                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                    />
                                    <label htmlFor="showBenefits" className="text-sm text-gray-700">
                                        Mostrar sección de beneficios
                                    </label>
                                </div>
                            </div>

                            {/* Información adicional */}
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                <div className="flex items-start">
                                    <FaCode className="text-orange-500 mt-1 mr-2 flex-shrink-0" />
                                    <div>
                                        <h4 className="text-sm font-medium text-orange-800 mb-1">Después de crear la configuración</h4>
                                        <p className="text-xs text-orange-600 mb-2">
                                            Podrás copiar el código de embed para integrarlo en tu sitio web.
                                        </p>
                                        <p className="text-xs text-orange-600">
                                            <strong>Tip:</strong> El código script es más flexible y permite personalización avanzada.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Botones */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-orange-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                                            Creando...
                                        </>
                                    ) : (
                                        <>
                                            <FaCode className="w-4 h-4 mr-2" />
                                            Crear Configuración
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MarcasTiendasPage; 